/** GPU Vibrance — fragment-shader port of
 *  `lib/adjustments/vibrance.ts`.
 *
 *  Two HSL-space scalers, both applied to S:
 *
 *  - vibrance: quadratic protective falloff. Positive boosts with
 *    `room² * shift`, where `room = 1 - sa` — dull pixels gain
 *    most, vivid pixels gain little. Negative desaturates
 *    uniformly (no protection).
 *  - saturation: same uniform scale as Hue/Sat — positive blends
 *    toward 1, negative scales toward 0.
 *
 *  Both transforms preserve hue and lightness; only S moves. */

import { RGB_TO_HSL_GLSL, HSL_TO_RGB_GLSL } from '../glsl-helpers';
import type { VibranceValue } from '../../adjustments/vibrance';
import { applyShader } from '../runtime';

const VIBRANCE_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform float u_vibrance;    // normalized [-1, 1] (raw / 100)
uniform float u_saturation;  // normalized [-1, 1] (raw / 100)
out vec4 outColor;
${RGB_TO_HSL_GLSL}
${HSL_TO_RGB_GLSL}

void main() {
  vec4 c = texture(u_source, v_uv);
  vec3 hsl = rgbToHsl(c.rgb);
  // Vibrance: positive uses (1 - sa)^2 falloff; negative is uniform.
  if (u_vibrance > 0.0) {
    float room = 1.0 - hsl.y;
    hsl.y = hsl.y + room * room * u_vibrance;
  } else if (u_vibrance < 0.0) {
    hsl.y = hsl.y * (1.0 + u_vibrance);
  }
  // Saturation: same shape as Hue/Sat.
  if (u_saturation > 0.0) hsl.y = hsl.y + (1.0 - hsl.y) * u_saturation;
  else if (u_saturation < 0.0) hsl.y = hsl.y * (1.0 + u_saturation);
  hsl.y = clamp(hsl.y, 0.0, 1.0);
  vec3 rgb = hslToRgb(hsl);
  rgb = clamp(floor(rgb * 255.0 + 0.5) / 255.0, 0.0, 1.0);
  outColor = vec4(rgb, c.a);
}
`;

export function applyVibranceOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: VibranceValue,
): boolean {
  return applyShader({
    source,
    target,
    fragShaderSrc: VIBRANCE_FRAG,
    uniforms: {
      u_vibrance: { type: 'float', value: value.vibrance / 100 },
      u_saturation: { type: 'float', value: value.saturation / 100 },
    },
  });
}
